// src/pages/Dashboard/Admin/Inventaire/components/InventaireFiltersCard.jsx

import React from "react";
import { FaPlus, FaSearch, FaSyncAlt } from "react-icons/fa";

export default function InventaireFiltersCard({
  filters,
  setFilters,
  categoryOptions = [],
  loading,
  onAdd,
  onRefresh,
}) {
  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetFilters = () => {
    setFilters({
      query: "",
      category: "",
      stockStatus: "",
      sort: "name_asc",
    });
  };

  const hasFilters =
    String(filters.query || "").trim() ||
    filters.category ||
    filters.stockStatus ||
    filters.sort !== "name_asc";

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Recherche & filtres
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Affiner l’inventaire
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Recherchez par nom, catégorie ou note, puis filtrez selon le
              niveau de stock.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaSyncAlt className={loading ? "animate-spin" : ""} />
              Actualiser
            </button>

            <button
              type="button"
              onClick={onAdd}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-3 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-[#012f6c]"
            >
              <FaPlus />
              Ajouter un objet
            </button>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Recherche
            </label>

            <div className="relative">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                value={filters.query}
                onChange={(event) => updateFilter("query", event.target.value)}
                placeholder="Nom, catégorie, note..."
                className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Catégorie
            </label>

            <select
              value={filters.category}
              onChange={(event) => updateFilter("category", event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            >
              <option value="">Toutes catégories</option>
              {categoryOptions.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Stock
            </label>

            <select
              value={filters.stockStatus}
              onChange={(event) =>
                updateFilter("stockStatus", event.target.value)
              }
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            >
              <option value="">Tous les stocks</option>
              <option value="ok">En stock</option>
              <option value="low">Stock faible ≤ 3</option>
              <option value="out">Rupture</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Tri
            </label>

            <select
              value={filters.sort}
              onChange={(event) => updateFilter("sort", event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            >
              <option value="name_asc">Nom A → Z</option>
              <option value="name_desc">Nom Z → A</option>
              <option value="category_asc">Catégorie A → Z</option>
              <option value="stock_desc">Stock décroissant</option>
              <option value="stock_asc">Stock croissant</option>
            </select>
          </div>
        </div>

        {hasFilters ? (
          <div className="mt-4">
            <button
              type="button"
              onClick={resetFilters}
              className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              Réinitialiser les filtres
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}